import { Component, type ErrorInfo, type ReactNode } from 'react';
import { getTracker } from '@/telemetry/tracker';

interface Props {
  children: ReactNode;
  /** Optional context tag for telemetry (e.g. the hosted appId). */
  context?: string;
  /**
   * Compact replacement UI for tight hosts (e.g. a header slot) where the
   * default full-width fallback would look broken. Replaces it entirely.
   */
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors in its subtree, logs them to AppInsights and shows
 * a non-fatal fallback so a crashing page / MFE host doesn't white-screen the
 * whole shell — the header and nav rail stay mounted. React equivalent of the
 * legacy global Angular `ErrorHandler` (RaptorUiErrorHandler) for the content
 * area; window-level uncaught errors are handled separately by
 * `installWindowTelemetry`.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    getTracker().error(error, {
      source: 'react_error_boundary',
      context: this.props.context ?? '',
      componentStack: info.componentStack ?? '',
    });
  }

  private reset = (): void => this.setState({ error: null });

  render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div role="alert" className="shell-error-boundary">
          <h2>Something went wrong</h2>
          <p>This area failed to render. Try again, or reload the page.</p>
          <button type="button" className="shell-error-boundary__retry" onClick={this.reset}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
